import { CircularProgress, Slider, Tooltip, Typography } from '@mui/joy';
import {
  ArrowsOut,
  ArrowUp,
  CaretDown,
  ChatText,
  ImageSquare,
} from '@phosphor-icons/react';
import clsx from 'clsx';
import { ReactNode, useState } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';

import { PolicyTabField } from './PolicyTabField';

// eslint-disable-next-line import/extensions
import defaultImage from '~/assets/png/defaultImage.png';
import defaultBanner from '~/assets/svg/defaultBanner.svg';
import { getFileExtension, isNotNull } from '~/assets/utils/helpers';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import {
  FilePicker,
  tofilePickerField,
  useFilePicker,
} from '~/components/ui/file-picker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/components/ui/popover';
import { isCurrentLanguageDefault } from '~/contexts/lang/languageContext';
import { getUserTypeName } from '~/features/auth/constants';
import { useTenantSlug } from '~/features/tenant/helpers';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { FileUploadService } from '~/services/api/file-upload/endpoint';
import { isMutationSuccess, useWrappedMutation } from '~/services/api/rest-api';
import { TenantService } from '~/services/api/tenant/endpoints';
import {
  faqSchema,
  languageSchema,
  TabType,
} from '~/services/api/tenant/types';
import { getCroppedImg } from '~/services/cropImageService';
import { useAuthSlice } from '~/slices/auth';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';
import { useForm } from '~/utils/forms';

const baseCustomTabSchema = z.object({
  id: z.number(),
  title: z.string().trim().min(1),
  is_new: z.boolean().optional().default(false),
  is_deleted: z.boolean().optional().default(false),
  shouldDisplayInOverview: z.boolean().optional().default(false),
});

const policyTab = baseCustomTabSchema.extend({
  type: z.literal(TabType.POLICY),
  content: z.string().trim().min(1),
});

const overviewTab = baseCustomTabSchema.extend({
  type: z.literal(TabType.INFOGRAPHIC),
  content: z.string(),
  selectedPreset: z
    .object({
      url: z.string(),
      id: z.number(),
    })
    .optional()
    .nullable(),
  asset: tofilePickerField().optional(),
});

const regularTab = baseCustomTabSchema.extend({
  type: z.literal(TabType.REGULAR),
  content: z.string().trim().min(1),
});

const videoTab = baseCustomTabSchema.extend({
  type: z.literal(TabType.VIDEO),
  content: z.string().trim(),
  asset: tofilePickerField().optional(),
});

const pdfTab = baseCustomTabSchema.extend({
  type: z.literal(TabType.PDF),
  content: z.string().trim(),
  asset: tofilePickerField().optional(),
});

const faqTab = baseCustomTabSchema.extend({
  type: z.literal(TabType.FAQ),
  content: z.array(faqSchema),
});

const schema = z.object({
  isEditing: z.boolean().optional().default(false),
  enableComments: z.boolean().optional().default(false),
  logo: tofilePickerField(),
  isLogoRectangle: z.boolean(),
  banner: tofilePickerField(),
  LocalizedBanners: z.array(
    z.object({
      language_id: z.number(),
      file: z.instanceof(File).optional(),
      url: z.string().optional(),
    }),
  ),
  baseLanguage: z.number(),
  languages: z.array(languageSchema),
  channelDetailsId: z.number().nullable(),
  customTabs: z
    .array(
      z.discriminatedUnion('type', [
        policyTab,
        overviewTab,
        regularTab,
        videoTab,
        pdfTab,
        faqTab,
      ]),
    )
    .superRefine((tabs, ctx) => {
      tabs.forEach((tab, index) => {
        // Special validation for video tabs if tabs is new
        if (tab.type === TabType.VIDEO || tab.type === TabType.PDF) {
          if (tab.is_new && !tab.asset?.file) {
            ctx.addIssue({
              path: [`${index}.asset.file`],
              message: `Please upload file for ${tab.title}`,
              code: z.ZodIssueCode.custom,
            });
          }
        }
      });
    }),
});

type FormFields = z.infer<typeof schema>;
export const usePolicyForm = () => useFormContext<FormFields>();

export function CompanyPolicyForm({
  refetch,
  defaultValues,
}: Readonly<{
  refetch: () => void;
  defaultValues?: Partial<z.infer<typeof schema>>;
}>) {
  const userType = useAuthSlice().auth.user.type;
  const userTypeName = getUserTypeName(userType);
  const intl = useIntl();

  const form = useForm({
    schema,
    defaultValues,
  });
  const { createToastNotification } = useToastNotificationContext();
  const preventEditOrSaveToast = () => {
    createToastNotification(
      intl.formatMessage({
        defaultMessage:
          'Program page edits must be made in English. For other languages, use Program Settings > Language > Multi-Language Support.',
      }),
      'error',
    );
  };
  const [isLoading, setIsLoading] = useState(false);
  const [reloadAssets, setReloadAssets] = useState(false);
  const slug = useTenantSlug();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const isEditing = form.watch('isEditing', false);
  const toggleEditing = () => {
    if (!isCurrentLanguageDefault()) {
      preventEditOrSaveToast();
      return;
    }

    form.setValue('isEditing', !isEditing);
  };

  const [fileUploadProcess] =
    FileUploadService.endpoints.UploadLogoBanerProcess.useMutation();
  const [uploadLocalizedBanner] =
    FileUploadService.endpoints.UploadLocalizedBanerProcess.useMutation();
  const [tenantLogoUpload] =
    FileUploadService.endpoints.uploadTenantLogoProcess.useMutation();
  const [tenantInfographicUpload] =
    FileUploadService.endpoints.uploadTenantInfographicProcess.useMutation();
  const updateTenant = useWrappedMutation(
    TenantService.endpoints.updateTenant.useMutation(),
  );

  const updateChannelTabs = useWrappedMutation(
    TenantService.endpoints.updateChannelTabs.useMutation(),
  );

  const uploadProgramFile = async (
    file: File | undefined,
    isDisplayed: boolean,
  ) => {
    if (file) {
      const uploadResult = await tenantInfographicUpload({
        file: file,
        startUploadData: {
          is_display_on_program_overview_enabled: isDisplayed,
          file_name: file.name,
          file_type: getFileExtension(file.name),
        },
        selectedChannelId: selectedChannel ?? 0,
        slug: slug,
      });
      if (isMutationSuccess(uploadResult)) {
        const { url } = uploadResult.data;
        if (url) return url;
      }
    }
  };

  const getTabDataforPatch = async (
    data: z.infer<typeof schema>['customTabs'][number],
    isNew: boolean,
  ) => {
    // IF REGULAR OR POLICY
    if (data.type === TabType.POLICY || data.type === TabType.REGULAR) {
      return {
        ...(!isNew && { id: data.id }),
        title: data.title,
        tab_type: data.type,
        content: data.content,
        is_display_on_program_overview_enabled: data.shouldDisplayInOverview,
      };
    }
    // IF INFOGRAPHIC
    if (data.type === TabType.INFOGRAPHIC) {
      const content =
        data.selectedPreset?.id.toString() ??
        (await uploadProgramFile(
          data.asset?.file,
          data.shouldDisplayInOverview,
        ));
      return {
        ...(!isNew && { id: data.id }),
        tab_type: data.type,
        ...(content && { content: content }),
        is_display_on_program_overview_enabled: data.shouldDisplayInOverview,
      };
    }

    // FAQ
    if (data.type === TabType.FAQ) {
      return {
        ...(!isNew && { id: data.id }),
        title: data.title,
        tab_type: data.type,
        content: data.content,
        is_display_on_program_overview_enabled: data.shouldDisplayInOverview,
      };
    }

    // VIDEO OR PDF
    if (data.type === TabType.VIDEO || data.type === TabType.PDF) {
      const content = await uploadProgramFile(
        data.asset?.file,
        data.shouldDisplayInOverview,
      );
      // add content and file_type if content has value otherwise udpate title only
      return {
        ...(!isNew && { id: data.id }),
        tab_type: data.type,
        title: data.title,
        ...(content && { content: content }),
        is_display_on_program_overview_enabled: data.shouldDisplayInOverview,
        ...(content && { file_type: getFileExtension(content) }),
      };
    }

    return null;
  };

  // UPDATE CUSTOM TABS
  const updateCustomTabs = async (data: FormFields) => {
    const addedTabsBody = await Promise.all(
      data.customTabs
        .filter((tab) => tab.is_new)
        .map(async (tab) => {
          const result = await getTabDataforPatch(tab, true);
          return result;
        }),
    ).then((result) => result.filter(isNotNull));
    const patchedTabsBody = await Promise.all(
      data.customTabs
        .filter((tab) => !tab.is_new && !tab.is_deleted)
        .map(async (tab) => {
          const result = await getTabDataforPatch(tab, false);
          return result;
        }),
    ).then((result) => result.filter(isNotNull));
    const deletedTabIDs = data.customTabs
      .filter((tab) => tab.is_deleted)
      .map((tab) => tab.id);

    const body = {
      ...(addedTabsBody?.length > 0 && { created_data: addedTabsBody }),
      ...(patchedTabsBody?.length > 0 && { tabs: patchedTabsBody }),
      ...(deletedTabIDs.length > 0 && { delete_tab_ids: deletedTabIDs }),
    };
    await updateChannelTabs.mutateAsync(
      {
        data: body,
        intakeChannelId: selectedChannel ?? 0,
      },
      {
        onError: () => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Unable to update company Program tabs.',
            }),
            'error',
          );
        },
      },
    );
  };

  // UPDATE BANNER
  const uploadBanner = async (data: FormFields) => {
    if (!data.banner.file) return null;

    await fileUploadProcess({
      selectedChannelId: selectedChannel ?? 0,
      slug: slug,
      uploadType: 'banner',
      file: data.banner.file,
      startUploadData: {
        file_name: data.banner.file.name,
        file_type: getFileExtension(data.banner.file.name),
      },
    });
  };

  // UPDATE LOGO
  const uploadLogoHandler = async (data: FormFields) => {
    if (!data.logo.file) return null;

    const uploadLogo = await tenantLogoUpload({
      file: data.logo.file,
      startUploadData: {
        file_name: data.logo.file.name,
        file_type: getFileExtension(data.logo.file.name),
      },
      slug: slug,
    });
    if (isMutationSuccess(uploadLogo)) {
      const { id } = uploadLogo.data;
      if (slug && id)
        await updateTenant.mutateAsync({
          slug: slug,
          logo: id,
          is_rectangular_logo_enabled: data.isLogoRectangle,
        });
    }
  };

  const updateTranslatedBanners = async (data: FormFields) => {
    if (!data.channelDetailsId) return;
    for (const banner of data.LocalizedBanners) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { file, language_id } = banner;
      if (!file) continue;
      await uploadLocalizedBanner({
        file: file,
        startUploadData: {
          file_name: file.name,
          file_type: getFileExtension(file.name),
        },
        slug: slug ?? '',
        selectedChannelId: selectedChannel ?? 0,
        main_id: data.channelDetailsId,
        language_id: language_id,
        type: 'channel',
      });
    }
  };

  const savePogramHandler = form.handleSubmit(async (data) => {
    if (!isCurrentLanguageDefault()) {
      preventEditOrSaveToast();
      return;
    }

    setIsLoading(true);
    await updateCustomTabs(data);
    await uploadBanner(data);
    await updateTranslatedBanners(data);
    await uploadLogoHandler(data);
    refetch();
    setIsLoading(false);
    toggleEditing();
  });

  return (
    <FormProvider {...form}>
      <Card sx={{ height: '100%' }}>
        <div>
          {isLoading && (
            <div className='fixed top-0 left-0 w-full h-full flex justify-center items-center bg-white bg-opacity-50 z-50'>
              <CircularProgress />
            </div>
          )}
        </div>
        <div>
          <div className='flex items-center justify-between'>
            <Typography level='title-lg'>
              {intl.formatMessage({
                defaultMessage: 'Company Policy & Logo',
              })}
            </Typography>
            {isEditing ? (
              <div className='flex flex-row gap-2'>
                <Button
                  color='neutral'
                  size='md'
                  variant='plain'
                  onClick={() => {
                    toggleEditing();
                    /* this will reload the <CompanyAssets> to reset the Cropper back to defaultValue Images
                     * clean up the uploaded images and reset back to defaultValue
                     */
                    form.reset();
                    setReloadAssets(true);
                    setTimeout(() => setReloadAssets(false), 0);
                  }}
                >
                  {intl.formatMessage({ defaultMessage: 'Cancel' })}
                </Button>
                <Button
                  color='gradient-primary'
                  type='submit'
                  size='md'
                  variant='outlined'
                  onClick={savePogramHandler}
                >
                  {intl.formatMessage({ defaultMessage: 'Save' })}
                </Button>
              </div>
            ) : (
              <div className='flex flex-row gap-2'>
                <Button
                  color='neutral'
                  size='md'
                  variant='outlined'
                  startDecorator={<ChatText />}
                  onClick={() =>
                    form.setValue(
                      'enableComments',
                      !form.getValues('enableComments'),
                    )
                  }
                >
                  {intl.formatMessage({ defaultMessage: 'Add Comment' })}
                </Button>
                {(userTypeName === 'PARTNER_ADMIN' ||
                  userTypeName === 'TENANT_ADMIN') && (
                  <Button
                    color='gradient-primary'
                    size='md'
                    variant='outlined'
                    onClick={toggleEditing}
                  >
                    {intl.formatMessage({ defaultMessage: 'Edit' })}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
        <div>{!reloadAssets && <CompanyAssets />}</div>
        <div className='pt-10'>
          <PolicyTabField />
        </div>
      </Card>
    </FormProvider>
  );
}

export function FilePickerTrigger({
  children,
  onClick,
}: {
  readonly children: ReactNode;
  readonly onClick?: () => void;
}) {
  const api = useFilePicker();

  const onClickCapture = () => {
    if (onClick) onClick();
  };

  return (
    <>
      <input {...api.hiddenInputProps} />
      <Button
        {...api.triggerProps}
        color='neutral'
        size='sm'
        variant='soft'
        onClickCapture={onClickCapture}
      >
        {children}
      </Button>
    </>
  );
}

const DEFAULT_ZOOM_LEVEL = 1;
const DEFAULT_CROP_AREA: Point = { x: 0, y: 0 };
export function CompanyAssets() {
  const form = usePolicyForm();
  const [isLogoRectangle, setIsLogoRectangle] = useState(
    form.getValues('isLogoRectangle'),
  );
  const [bannerLanguage, setBannerLanguage] = useState(
    form.getValues('baseLanguage'),
  );
  const [sampleBanner, setSampleBanner] = useState<string | undefined>(
    form.getValues('banner.url'),
  );
  const [sampleLogo, setSampleLogo] = useState<string | undefined>(
    form.getValues('logo.url'),
  );
  const [previewedBanner, setPreviewedBanner] = useState<{
    language_id: number;
    url?: string;
  }>({
    language_id: form.getValues('baseLanguage'),
    url: form.getValues('banner.url'),
  });

  const intl = useIntl();

  const isEditing = form.watch('isEditing');
  const languages = form.watch('languages');
  const LocalizedBanners = form.watch('LocalizedBanners');
  const baseLanguage = form.watch('baseLanguage');

  return (
    <div className='relative h-full flex flex-col pb-14'>
      {isEditing ? (
        <Controller
          name='banner'
          control={form.control}
          render={({ field }) => (
            <FilePicker
              accept='image/*'
              onFileAccept={(details) => {
                if (details.files.length === 0) return;
                setSampleBanner(URL.createObjectURL(details.files[0]));
              }}
            >
              <BannerUploadCropper
                url={sampleBanner ?? null}
                onChange={(file, url) => {
                  if (bannerLanguage === form.watch('baseLanguage'))
                    form.setValue('banner', { file, url });
                  else {
                    form.setValue(
                      'LocalizedBanners',
                      LocalizedBanners.map((banner) => {
                        if (banner.language_id === bannerLanguage)
                          return {
                            ...banner,
                            file: file,
                            url: url,
                          };
                        else return banner;
                      }),
                    );
                  }
                }}
                isEditing={isEditing}
                isNewFile={!!field.value.file}
              >
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant='solid'
                      startDecorator={<ArrowUp weight='fill' />}
                    >
                      {intl.formatMessage({
                        defaultMessage: 'Upload Image',
                      })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className='z-50'>
                    <div className='flex flex-col gap-2'>
                      <FilePickerTrigger
                        onClick={() => {
                          setBannerLanguage(baseLanguage);
                        }}
                      >
                        {intl.formatMessage({
                          defaultMessage: 'Default Banner',
                        })}
                      </FilePickerTrigger>
                      {/* UPLOAD LANGUAGE SPECIFIC BANNER */}
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            variant='soft'
                            color='neutral'
                            disabled={languages.length === 1}
                          >
                            {intl.formatMessage({
                              defaultMessage: 'Upload translated banner',
                            })}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className='z-50' side='right'>
                          <div className='flex flex-col gap-2'>
                            {languages.map((language) => {
                              if (baseLanguage !== language.id)
                                return (
                                  <FilePickerTrigger
                                    key={language.id}
                                    onClick={() => {
                                      setBannerLanguage(language.id);
                                    }}
                                  >
                                    {language.name}{' '}
                                    {bannerLanguage === language.id &&
                                      intl.formatMessage({
                                        defaultMessage: '(current)',
                                      })}
                                  </FilePickerTrigger>
                                );
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </PopoverContent>
                </Popover>
              </BannerUploadCropper>
            </FilePicker>
          )}
        />
      ) : (
        <>
          <div className='aspect-w-16 aspect-h-4'>
            <img src={previewedBanner.url ?? defaultBanner} alt='banner' />
          </div>
          <div className='absolute top-4 right-4'>
            <Popover>
              <PopoverTrigger asChild>
                <Tooltip title='Change Banner Language'>
                  <Button variant='solid' color='secondary' size='sm'>
                    <CaretDown />
                  </Button>
                </Tooltip>
              </PopoverTrigger>
              <PopoverContent className='z-50'>
                <div className='flex flex-col gap-2'>
                  <Button
                    color='neutral'
                    variant='soft'
                    onClick={() => {
                      setPreviewedBanner({
                        language_id: form.watch('baseLanguage'),
                        url: form.watch('banner.url'),
                      });
                    }}
                  >
                    <FormattedMessage defaultMessage='Default' />
                    {form.watch('baseLanguage') ===
                      previewedBanner.language_id && (
                      <FormattedMessage defaultMessage='( Current )' />
                    )}
                  </Button>
                  {languages.map((language) => {
                    if (baseLanguage !== language.id)
                      return (
                        <Button
                          key={language.id}
                          color='neutral'
                          variant='soft'
                          onClick={() => {
                            setPreviewedBanner({
                              language_id: language.id,
                              url:
                                form
                                  .watch('LocalizedBanners')
                                  .find(
                                    (banner) =>
                                      banner.language_id === language.id,
                                  )?.url || form.watch('banner.url'),
                            });
                          }}
                        >
                          {language.name}
                          {language.id === previewedBanner.language_id && (
                            <FormattedMessage defaultMessage='( Current )' />
                          )}
                        </Button>
                      );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </>
      )}

      <div>
        <Controller
          control={form.control}
          name='logo'
          render={({ field }) => (
            <FilePicker
              accept='image/png'
              onFileAccept={(details) => {
                if (details.files.length === 0) return;
                setSampleLogo(URL.createObjectURL(details.files[0]));
                form.setValue('isLogoRectangle', isLogoRectangle);
              }}
            >
              <UploadLogoCropper
                url={sampleLogo ?? null}
                onChange={(file, url) => form.setValue('logo', { file, url })}
                isEditing={isEditing}
                isNewFile={!!field.value.file}
                isRect={form.watch('isLogoRectangle')}
              >
                <Popover modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      variant='soft'
                      startDecorator={<ArrowUp weight='fill' />}
                    >
                      {intl.formatMessage({
                        defaultMessage: 'Upload Logo',
                      })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className='z-50'>
                    <div className='flex flex-col gap-2'>
                      <FilePickerTrigger
                        onClick={() => {
                          setIsLogoRectangle(false);
                        }}
                      >
                        {intl.formatMessage({
                          defaultMessage: 'Circular Logo',
                        })}
                      </FilePickerTrigger>
                      <FilePickerTrigger
                        onClick={() => {
                          setIsLogoRectangle(true);
                        }}
                      >
                        {intl.formatMessage({
                          defaultMessage: 'Rectangular Logo',
                        })}
                      </FilePickerTrigger>
                    </div>
                  </PopoverContent>
                </Popover>
              </UploadLogoCropper>
            </FilePicker>
          )}
        />
      </div>
    </div>
  );
}

export function BannerUploadCropper({
  url,
  onChange,
  isEditing,
  isNewFile,
  children,
}: Readonly<{
  url: string | null;
  onChange: (file: File, url: string) => void;
  isEditing: boolean;
  isNewFile: boolean;
  children: ReactNode;
}>) {
  const intl = useIntl();
  const [crop, setCrop] = useState(DEFAULT_CROP_AREA);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM_LEVEL);

  const handleCropBannerComplete = async (_: Area, croppedAreaPixels: Area) => {
    if (!url) return;

    const croppedBannerFile = await getCroppedImg(url, croppedAreaPixels);
    if (!croppedBannerFile) return;
    onChange(croppedBannerFile, URL.createObjectURL(croppedBannerFile));
  };

  return (
    <div className='relative h-full pb-5'>
      <div className='relative group'>
        <div className='aspect-w-16 aspect-h-4'>
          <Cropper
            aspect={16 / 4}
            crop={crop}
            zoom={zoom}
            showGrid={false}
            objectFit='cover'
            image={url ?? defaultBanner}
            onCropChange={setCrop}
            onCropComplete={handleCropBannerComplete}
            onZoomChange={setZoom}
            cropShape='rect'
          />
        </div>
        {isEditing && isNewFile && (
          <div className='absolute inset-0 opacity-100 group-hover:opacity-0 pointer-events-none bg-zinc-400/50 flex items-center justify-center transition'>
            <Typography startDecorator={<ArrowsOut />}>
              {intl.formatMessage({
                defaultMessage: 'Drag to reposition',
              })}
            </Typography>
          </div>
        )}
        {isEditing && (
          <div className='absolute inset-x-0 justify-end items-center bottom-0 px-3 py-2 flex gap-4'>
            {children}
          </div>
        )}
      </div>
      {isEditing && (
        <div className='flex justify-center'>
          <div className='max-w-52 shrink-0 w-full flex items-center gap-4'>
            <ImageSquare weight='fill' />
            <Slider
              size='sm'
              min={1}
              max={10}
              onChange={(_, value) => !Array.isArray(value) && setZoom(value)}
            />
            <ImageSquare weight='fill' className='size-8' />
          </div>
        </div>
      )}
    </div>
  );
}

export function UploadLogoCropper({
  url,
  onChange,
  isEditing,
  isNewFile,
  children,
  isRect,
}: Readonly<{
  url: string | null;
  onChange: (file: File, url: string) => void;
  isEditing: boolean;
  isNewFile: boolean;
  children: ReactNode;
  isRect: boolean;
}>) {
  const [crop, setCrop] = useState(DEFAULT_CROP_AREA);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM_LEVEL);

  const handleCropLogoComplete = async (_: Area, croppedAreaPixels: Area) => {
    if (!url) return;

    const croppedLogoFile = await getCroppedImg(url, croppedAreaPixels);
    if (!croppedLogoFile) return;
    onChange(croppedLogoFile, URL.createObjectURL(croppedLogoFile));
  };

  return (
    <div
      className={clsx([
        'flex flex-col justify-center items-center absolute left-0 px-10',
        !isEditing && isRect && 'bottom-4',
        !isEditing && !isRect && 'bottom-0',
        isEditing && isRect && 'bottom-14',
        isEditing && !isRect && 'bottom-10',
      ])}
    >
      <div
        className={clsx(
          isRect ? 'rounded-2xl w-48 h-28' : 'rounded-full size-40',
          'relative size-40 overflow-hidden',
        )}
      >
        <div
          className={clsx(
            isRect ? 'rounded-2xl w-48 h-28' : 'rounded-full size-40',
            'relative rounded-sm bg-white border border-white overflow-hidden',
          )}
        >
          {isEditing ? (
            <Cropper
              aspect={isRect ? 6.5 / 4 : 1}
              crop={crop}
              zoom={zoom}
              showGrid={false}
              objectFit='cover'
              image={url ?? defaultImage}
              onCropChange={setCrop}
              onCropComplete={handleCropLogoComplete}
              onZoomChange={setZoom}
              cropShape={isRect ? 'rect' : 'round'}
            />
          ) : (
            <img
              alt='logo'
              src={url ?? defaultImage}
              className='w-full h-full object-cover'
            />
          )}
        </div>
        {isEditing && isNewFile && (
          <div className='absolute inset-0 opacity-100 group-hover:opacity-0 pointer-events-none bg-zinc-400/50 flex items-center justify-center transition'>
            <Typography startDecorator={<ArrowsOut />}></Typography>
          </div>
        )}
      </div>
      {isEditing && (
        <div className='absolute left-0 bottom-0 translate-y-full w-full flex justify-center pt-2'>
          {children}
        </div>
      )}
    </div>
  );
}
